import * as React from 'react'
import { UserFeedbackComplete } from './UserFeedbackComplete'

import {
  Button,
  EmojiDissatisfiedIcon,
  EmojiNeutralIcon,
  EmojiSmilingIcon,
  Flex,
  Form,
  FormFooter,
  FormHeader,
  FormRow,
  Icon,
  RadioButton,
  RadioGroup,
  Textarea,
} from '@grammarly/design-system'

import classes from './UserFeedback.module.scss'

const feedbackOptions = [
  {
    label: 'Dissatisfied',
    icon: EmojiDissatisfiedIcon,
    value: 'bad',
  },
  {
    label: 'Neutral',
    icon: EmojiNeutralIcon,
    value: 'ok',
  },
  {
    label: 'Satisfied',
    icon: EmojiSmilingIcon,
    value: 'good',
  },
]

type Mood = 'bad' | 'ok' | 'good'

type Props = {
  readonly onSubmit: (data: { mood: Mood; message: string }) => void
  readonly onClose: () => void
}

export function UserFeedback({ onSubmit, onClose }: Props) {
  const [mood, setMood] = React.useState<Mood | undefined>()
  const [message, setMessage] = React.useState('')

  const [submitted, setSubmitted] = React.useState(false)

  const handleSubmit = () => {
    if (!mood || !message.trim()) {
      return
    }

    onSubmit({ mood, message })
    setSubmitted(true)
  }

  if (submitted) {
    return <UserFeedbackComplete onClose={onClose} />
  }

  const isDataReady = mood && message.trim()

  return (
    <Flex align="center" justify="center" className={classes.userFeedback}>
      <Form onSubmit={handleSubmit} maxWidth={1000} spacing="spacious">
        <FormHeader heading="How do you like Grammarly?" headingLevel="h2" headingSize="small" />
        <FormRow>
          <RadioGroup
            legend="Choose a response"
            layout="row"
            isRequired
            className={classes.responseRadioGroup}
            value={mood}
            onChange={value => setMood(value as Mood)}
          >
            {feedbackOptions.map(option => (
              <RadioButton
                key={option.value}
                value={option.value}
                aria-label={`Select ${option.label}`}
              >
                <Flex
                  align="center"
                  justify="center"
                  direction="column"
                  gap={1}
                  className={[
                    classes.responseRadioItem,
                    option.value === mood ? classes.responseRadioChecked : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <Icon icon={option.icon} accessibilityLabel="" size="large" />
                  {option.label}
                </Flex>
              </RadioButton>
            ))}
          </RadioGroup>
        </FormRow>
        <FormRow>
          <Textarea
            label="Tell us why"
            isRequired
            value={message}
            onChange={setMessage}
            placeholder="Placeholder text"
            rows={5}
          />
        </FormRow>
        <FormFooter>
          <Button text="Submit" type="submit" width="full" isDisabled={!isDataReady} size="large" />
        </FormFooter>
      </Form>
    </Flex>
  )
}
